<template>
  <div class="p-grid">
    <div class="p-col-12">
      <Card class="p-m-2" v-bind="$attrs">
        <template #title="">
          <div class="p-d-flex p-justify-between">
            Config Service
            <div>
              <Button
                icon="pi pi-plus"
                label="เพิ่มใหม่"
                @click="onNew"
              ></Button>
            </div>
          </div>
        </template>
        <template #content="">
          <DataTable class="p-datatable-sm" :value="services">
            <template #empty="">ไม่พบข้อมูล</template>
            <Column
              header="Id"
              field="id"
              headerStyle="width:5rem"
              bodyStyle="width:5rem"
            ></Column>
            <Column
              header="Name"
              field="name"
              headerStyle="width:15rem"
              bodyStyle="width:15rem"
            ></Column>
            <Column
              header="Prefix"
              field="messagePrefix"
              headerStyle="width:10rem"
              bodyStyle="width:10rem"
            ></Column>
            <Column header="Endpoint" field="receivedMessageEndpoint"></Column>

            <Column
              header="JWT Token"
              field="jwtToken"
              headerStyle="width:5rem"
              bodyStyle="width:5rem"
            >
              <template #body="props">
                <Button
                  icon="pi pi-copy"
                  class="p-button-text"
                  @click="copyToken(props.data.jwtToken)"
                ></Button>
              </template>
            </Column>
            <Column headerStyle="width:5rem" bodyStyle="width:5rem">
              <template #body="props">
                <Button
                  class="p-button-rounded"
                  icon="pi pi-pencil"
                  @click="onEdit(props.data)"
                ></Button>
              </template>
            </Column>
          </DataTable>
        </template>
      </Card>
    </div>
    <div class="p-col-12">
      <div class="p-panel">
        <div class="p-panel-header">
          <div class="p-panel-title">
            API List
          </div>
        </div>
        <div class="p-panel-content">
          api/v1/Service/status/:employeeId = query ข้อมูลว่าได้ทำการ
          เชื่อมต่อกับ line เรียบร้อยแล้วหรือยัง
          <br />Result
          <pre>
            {"isLinked":true,"registerLink":"https://liff.line.me/1655989436-6qo0ePEY"}
          </pre>
          isLinked = เชื่อมต่อแล้วหรือไม่<br>
          registerLink = ในกรณียังไม่เชื่อมต่อ ค่านี้้จะเก็บ url สำหรับ connect line.
        </div>
      </div>
    </div>
  </div>
  <Dialog
    v-model:visible="showNewDialog"
    :header="service.id === 0 ? 'เพิ่มใหม่' : `แก้ไข ${service.id}`"
    modal
  >
    <div class="p-grid">
      <div class="p-col-12 p-md-6 p-fluid p-field">
        <label for="name">Name</label>
        <InputText
          id="name"
          v-model="service.name"
          :class="{ 'p-invalid': v$.service.name.$error }"
        ></InputText>
        <small
          class="p-error"
          v-for="error in v$.service.name.$errors"
          :key="error.$uid"
          >{{ error.$message }}
        </small>
      </div>
      <div class="p-col-12 p-md-6 p-fluid p-field">
        <label for="prefix">Prefix</label>
        <InputText
          id="prefix"
          v-model="service.messagePrefix"
          :class="{ 'p-invalid': v$.service.messagePrefix.$error }"
        ></InputText>
        <small
          class="p-error"
          v-for="error in v$.service.messagePrefix.$errors"
          :key="error.$uid"
          >{{ error.$message }}
        </small>
      </div>
      <div class="p-col-12 p-fluid p-field">
        <label for="EndPoint">End Point</label>
        <InputText
          id="EndPoint"
          v-model="service.receivedMessageEndpoint"
          :class="{ 'p-invalid': v$.service.receivedMessageEndpoint.$error }"
        ></InputText>
        <small
          class="p-error"
          v-for="error in v$.service.receivedMessageEndpoint.$errors"
          :key="error.$uid"
          >{{ error.$message }}
        </small>
      </div>
    </div>
    <template #footer="">
      <Button
        icon="pi pi-check"
        :label="service.id === 0 ? 'เพิ่มใหม่' : `แก้ไข`"
        class="p-button-success"
        @click="onSubmit(false)"
      ></Button>
      <Button
        icon="pi pi-times"
        label="ยกเลิก"
        class="p-button-warning"
        @click="onCancel"
      ></Button>
      <Button
        icon="pi pi-refresh"
        label="Regenerate Token"
        class="p-button-danger"
        v-if="service.id > 0"
        @click="onRegenerateToken"
      ></Button>
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import useState from "@/store";
import usePlugin from "@/plugins";
import {
  GetServicePartnerResponse,
  ServicePartner,
  UpsertServicePartnerRequest,
  UpsertServicePartnerResponse,
} from "@/types";
import { clone } from "ramda";
import { useVuelidate } from "@vuelidate/core";
import { required, url } from "@vuelidate/validators";
import { useClipboard } from "@vueuse/core";

export default defineComponent({
  name: "ConfigServiceScreen",
  setup() {
    const init: ServicePartner = {
      id: 0,
      name: "",
      messagePrefix: "",
      receivedMessageEndpoint: "",
      jwtToken: "",
      createOn: null,
      createBy: null,
      updateOn: null,
      updateBy: null,
    };
    const showNewDialog = ref(false);
    const state = useState();
    const { axios, success, error } = usePlugin();
    const services = ref<ServicePartner[]>([]);
    const service = ref<ServicePartner>(clone(init));

    const onNew = () => {
      service.value = clone(init);
      showNewDialog.value = true;
    };

    const loadDocument = async () => {
      state.value.isLoading = true;
      try {
        const result = await axios.post<GetServicePartnerResponse>(
          "api/v1/server/servicePartner/get",
          {}
        );
        services.value = result.data.servicePartners ?? [];
        success("ดึงข้อมูลสำเร็จ");
      } catch (e) {
        error(e);
      } finally {
        state.value.isLoading = false;
      }
    };

    const onSubmit = async (regenToken = false) => {
      v$.value.$reset();
      v$.value.$touch();
      if (!(await v$.value.$validate())) return;

      state.value.isLoading = true;
      try {
        const result = await axios.post<UpsertServicePartnerResponse>(
          "api/v1/server/servicePartner/upsert",
          {
            servicePartner: service.value,
            regenerateToken: regenToken,
          } as UpsertServicePartnerRequest
        );
        success("บันทึกข้อมูลแล้ว");
        showNewDialog.value = false;
        await loadDocument();
      } catch (e) {
        error(e);
      } finally {
        state.value.isLoading = false;
      }
    };

    const onRegenerateToken = async () => {
      await onSubmit(true);
    };

    const onCancel = () => {
      showNewDialog.value = false;
    };
    const onEdit = (data: ServicePartner) => {
      service.value = clone(data);
      showNewDialog.value = true;
    };

    const clipboard = useClipboard();
    const copyToken = (token: string) => {
      clipboard.copy(token);
      success("Token copied !!!");
    };

    const rules = {
      service: {
        name: { required },
        messagePrefix: { required },
        receivedMessageEndpoint: { required, url },
      },
    };

    const v$ = useVuelidate(rules, { service });

    onMounted(() => loadDocument());
    return {
      showNewDialog,
      onNew,
      onCancel,
      services,
      service,
      onSubmit,
      onEdit,
      v$,
      copyToken,
      onRegenerateToken,
    };
  },
});
</script>

<style></style>
